import type { DrawNumber } from '../../../schema/draw/entry.js';
import type { Definition } from '../../../schema/frameworks/entry.js';
import type { ActivatedDrawId } from '../../../schema/marketplace/entry.js';
import { SessionStoreKey, type SettingsKeys } from '../../../schema/sessionstore/entry.js';
import type { EventId } from '../../../schema/sport/entry.js';
import type { WagerId, WagerStatus } from '../../../schema/wager/entry.js';
import type { DEFAULT_DRAW_ID } from '../index.js';
import type { ResultFilter } from './types.js';

export type WagersQueryDetails = {
  status: WagerStatus;
  count?: number;
  pagingSortKey?: string;
};

export type DrawQueryOptions = {
  product: Definition['drawProducts'][number];
  drawNumber: DrawNumber;
};

export type DefaultedDrawQueryOptions = {
  product: DrawQueryOptions['product'];
  drawNumber: DrawQueryOptions['drawNumber'] | typeof DEFAULT_DRAW_ID;
};

// ----- QUERY KEY FACTORIES -----
// All keys should follow this structure to keep it maintainable while adding and removing query keys
// Query keys is utilized in useQuery fetch functions to keep a 1:1 relation with cache keys and the fetch function to perform query invalidation.
// The factories is used everywhere in the application internally in the library; fetchers (queryFn), query invalidation etc.
// Every factory should have an "all" property to invalidate every query at once.
// A tanstack hook (eg useQuery) should utilize the query keys to perform a query via the tanstack QueryFunctionContext type so the query keys and fetch fn values doesn't diverge.
// All query keys must be in valid JSON serializable format. Each query key factory should have its own unique "scope"
//
// Read more: https://tkdodo.eu/blog/effective-react-query-keys
// And on QueryFunctionContext: https://tkdodo.eu/blog/leveraging-the-query-function-context
// Query keys: https://tanstack.com/query/v5/docs/framework/react/guides/query-keys

export const drawQueryKeys = {
  all: [{ scope: 'draws' }] as const,
  opened: (filterType: 'opened' | 'opened-products' = 'opened') =>
    [{ ...drawQueryKeys.all[0], filter: filterType }] as const,
  openedProductDraws: (product: Definition['moduleEngine']) =>
    [{ ...drawQueryKeys.opened('opened-products')[0], product }] as const,
  // "product" might not include "TopptipsetStryk" (only Topptipset), queryOptions.product in the other way includes it
  openedDraws: (product: Definition['moduleEngine']) => [{ ...drawQueryKeys.opened()[0], product }] as const,
  draw: (product: Definition['drawProducts'][number], drawNumber: DrawNumber) =>
    [{ ...drawQueryKeys.all[0], drawNumber, product }] as const,
} as const;

export const drawResultQueryKeys = {
  all: [{ scope: 'drawResult' }] as const,
  result: (product: Definition['drawProducts'][number], drawNumber: DrawNumber) =>
    [{ ...drawResultQueryKeys.all[0], product, drawNumber }] as const,
};

export const settingsQueryKeys = {
  all: [{ scope: SessionStoreKey.COUPON_SETTINGS }],
  product: (product: Definition['moduleEngine'], settingsKey: SettingsKeys) =>
    [{ ...settingsQueryKeys.all[0], product, settingsKey }] as const,
} as const;

export const sportQueryKeys = {
  all: [{ scope: 'sport' }] as const,
  sportEvent: (eventId: EventId) => [{ ...sportQueryKeys.all[0], eventId, type: 'sportEvent' }] as const,
  sportEventOdds: (eventId: EventId) => [{ ...sportQueryKeys.all[0], eventId, type: 'sportEventOdds' }] as const,
};

export const wagerQueryKeys = {
  all: [{ scope: 'wager' }] as const,
  wager: (wagerId: WagerId) => [{ ...wagerQueryKeys.all[0], wagerId }] as const,
  wagerList: (product: Definition['moduleEngine'], queryDetails: WagersQueryDetails) =>
    [{ ...wagerQueryKeys.all[0], product, queryDetails }] as const,
  // Use detail property to add details to the corresponding wagerId
  bestBetRows: () => [{ ...wagerQueryKeys.all[0], detail: 'bestBetRows' }] as const,
  wagerBestBetRows: (wagerId: WagerId, result: ResultFilter, maxRows?: number) =>
    [{ ...wagerQueryKeys.wager(wagerId)[0], ...wagerQueryKeys.bestBetRows()[0], result, maxRows }] as const,
  outcomeDistribution: (wagerId: WagerId, outcomeCount: number) =>
    [{ ...wagerQueryKeys.wager(wagerId)[0], outcomeCount, detail: 'outcomeDistribution' }] as const,
  totalNumberOfRows: (wagerId: WagerId) =>
    [{ ...wagerQueryKeys.wager(wagerId)[0], detail: 'totalNumberOfRows' }] as const,
};

export const analysisQueryKeys = {
  all: [{ scope: 'analysis' }] as const,
  gameAnalysis: (product: Definition['moduleEngine'], drawNumber: DrawNumber) =>
    [{ ...analysisQueryKeys.all[0], product, drawNumber }] as const,
};

export const accountBalanceQueryKeys = {
  all: [{ scope: 'accountBalance' }] as const,
  // WALLET_PLAYER is from WalletType in packages/poolbetting/src/schema/frameworks/Wallet.ts
  player: () => [{ ...accountBalanceQueryKeys.all[0], walletType: 'WALLET_PLAYER' }] as const,
} as const;

export const marketplaceQueryKeys = {
  all: [{ scope: 'marketplace' }] as const,
  marketplaceBalance: (product: Definition['drawProducts'][number], drawNumber: DrawNumber) =>
    [{ ...marketplaceQueryKeys.all[0], product, drawNumber }] as const,
  // Separate key so a skipped lookup never writes an empty result onto the real balance entry.
  marketplaceBalanceSkipped: (product: Definition['drawProducts'][number], drawNumber: DrawNumber) =>
    [{ ...marketplaceQueryKeys.all[0], product, drawNumber, skipped: true }] as const,
  retailerBalance: (activatedDrawId: ActivatedDrawId) => [{ ...marketplaceQueryKeys.all[0], activatedDrawId }] as const,
} as const;
